import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createHospital,
  getHospital,
  updateHospital,
  CreateHospitalPayload,
  Hospital,
  HospitalStatus,
  OnboardingStatus,
  HOSPITAL_TYPES,
  TIMEZONES,
} from "../../services/superAdmin.service";
import { Save, Loader2, RotateCcw } from "lucide-react";
import CustomSelect from "../../components/common/CustomSelect";
import {
  getAllPlans,
  getPlanPlatformTypes,
  type SubscriptionPlan,
} from "../../services/subscriptionService";

const INITIAL_FORM: CreateHospitalPayload = {
  hospitalName: "",
  hospitalCode: "",
  registrationNumber: "",
  hospitalType: "",
  domainName: "",
  officialEmail: "",
  officialPhone: "",
  addressline1: "",
  addressline2: "",
  city: "",
  state: "",
  country: "",
  postcode: "",
  timezone: "",
  subscriptionPlanId: "",
  contactPersonName: "",
  contactPersonEmail: "",
  contactPersonPhone: "",
};

const HospitalForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<CreateHospitalPayload>(INITIAL_FORM);
  const [savedForm, setSavedForm] = useState<CreateHospitalPayload>(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [status, setStatus] = useState<HospitalStatus>("ACTIVE");
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus>("NOT_STARTED");
  const [hospitalStatus, setHospitalStatus] = useState<HospitalStatus>("ACTIVE");
  const [hospitalOnboardingStatus, setHospitalOnboardingStatus] = useState<OnboardingStatus>("NOT_STARTED");
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    getAllPlans()
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load subscription plans"))
      .finally(() => setPlansLoading(false));
  }, []);

  const hospitalPlans = plans.filter(
    (plan) => plan.isActive && getPlanPlatformTypes(plan).includes("hospital")
  );

  useEffect(() => {
    if (isEdit && id) {
      getHospital(id)
        .then((h: Hospital) => {
          const data: CreateHospitalPayload = {
            hospitalName: h.hospitalName || "",
            hospitalCode: h.hospitalCode || "",
            registrationNumber: h.registrationNumber || "",
            hospitalType: h.hospitalType || "",
            domainName: h.domainName || "",
            officialEmail: h.officialEmail || "",
            officialPhone: h.officialPhone || "",
            addressline1: h.addressline1 || "",
            addressline2: h.addressline2 || "",
            city: h.city || "",
            state: h.state || "",
            country: h.country || "",
            postcode: h.postcode || "",
            timezone: h.timezone || "",
            subscriptionPlanId: h.subscriptionPlanId || "",
            contactPersonName: h.contactPersonName || "",
            contactPersonEmail: h.contactPersonEmail || "",
            contactPersonPhone: h.contactPersonPhone || "",
          };
          setForm(data);
          setSavedForm(data);
          if (h.status) {
            setStatus(h.status);
            setHospitalStatus(h.status);
          }
          if (h.onboardingStatus) {
            setOnboardingStatus(h.onboardingStatus);
            setHospitalOnboardingStatus(h.onboardingStatus);
          }
        })
        .catch(() => {
          toast.error("Hospital not found");
          navigate("/hospitals");
        })
        .finally(() => setFetching(false));
    }
  }, [id, isEdit, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleReset = () => {
    setForm(isEdit ? savedForm : INITIAL_FORM);
    if (isEdit) {
      setStatus(hospitalStatus);
      setOnboardingStatus(hospitalOnboardingStatus);
    }
    setErrors({});
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!form.hospitalName.trim()) errs.hospitalName = "Hospital name is required";
    else if (form.hospitalName.trim().length > 255) errs.hospitalName = "Must be 255 characters or less";

    if (!form.hospitalCode.trim()) errs.hospitalCode = "Hospital code is required";
    else if (form.hospitalCode.trim().length > 50) errs.hospitalCode = "Must be 50 characters or less";
    else if (!/^[a-zA-Z0-9\-]+$/.test(form.hospitalCode.trim()))
      errs.hospitalCode = "Only letters, numbers, and hyphens allowed";

    if (!form.domainName.trim()) errs.domainName = "Domain name is required";
    else if (!/^[a-z0-9]+$/.test(form.domainName.trim()))
      errs.domainName = "Must contain only lowercase letters and numbers";

    if (!isEdit && !form.subscriptionPlanId) errs.subscriptionPlanId = "Subscription plan is required";

    if (!form.officialEmail.trim()) errs.officialEmail = "Official email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.officialEmail.trim()))
      errs.officialEmail = "Invalid email format";
    else if (form.officialEmail.trim().length > 255) errs.officialEmail = "Must be 255 characters or less";

    if (!form.officialPhone.trim()) errs.officialPhone = "Official phone is required";
    else if (!/^[\+]?[\d\s\-\(\)]{7,20}$/.test(form.officialPhone.trim()))
      errs.officialPhone = "Invalid phone number format";

    if (!form.subscriptionPlanId?.trim()) errs.subscriptionPlanId = "Subscription plan is required";

    if (form.contactPersonEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactPersonEmail.trim()))
      errs.contactPersonEmail = "Invalid contact email format";

    if (form.contactPersonPhone && !/^[\+]?[\d\s\-\(\)]{7,20}$/.test(form.contactPersonPhone.trim()))
      errs.contactPersonPhone = "Invalid contact phone format";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (isEdit && id) {
        const updated = await updateHospital(id, { ...form, status, onboardingStatus });
        toast.success("Hospital updated successfully");
        if (updated?.emailNotification?.status === "FAILED") {
          toast.error(
            `Activation email could not be sent to ${updated.emailNotification.recipientEmail || "the admin"}. ${updated.emailNotification.errorMessage || ""}`.trim(),
            { duration: 6000 }
          );
        }
        navigate("/hospitals");
      } else {
        await createHospital(form);
        toast.success("Hospital created successfully");
        navigate("/hospitals");
      }
    } catch (err: any) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        setErrors(apiErrors);
        toast.error("Please fix the validation errors");
      } else {
        toast.error(err?.response?.data?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500">
        <button onClick={() => navigate("/hospitals")} className="hover:text-gray-900 transition-colors">
          Hospitals
        </button>
        <span className="text-gray-400">/</span>
        <span className="text-gray-900 font-medium">{isEdit ? "Edit" : "Add New"}</span>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-5">
          {/* General Information */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">General Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Hospital Name" name="hospitalName" value={form.hospitalName} onChange={handleChange} error={errors.hospitalName} required />
              <FormField label="Hospital Code" name="hospitalCode" value={form.hospitalCode} onChange={handleChange} error={errors.hospitalCode} required placeholder="e.g. HOS001" />
              <FormField label="Registration Number" name="registrationNumber" value={form.registrationNumber || ""} onChange={handleChange} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hospital Type</label>
                <CustomSelect
                  value={form.hospitalType || ""}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, hospitalType: val }));
                    if (errors.hospitalType) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.hospitalType;
                        return next;
                      });
                    }
                  }}
                  options={HOSPITAL_TYPES.map((t) => ({ value: t, label: t }))}
                  placeholder="Select type"
                />
              </div>
              {!isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Plan <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  {plansLoading ? (
                    <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                      <Loader2 size={16} className="animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <CustomSelect
                      value={form.subscriptionPlanId || ""}
                      onChange={(val) => {
                        setForm((prev) => ({ ...prev, subscriptionPlanId: val }));
                        if (errors.subscriptionPlanId) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.subscriptionPlanId;
                            return next;
                          });
                        }
                      }}
                      options={hospitalPlans.map((p) => ({
                        value: p.id,
                        label: `${p.planName} (${p.price} ${p.currency} / ${p.durationValue} ${p.durationType})`,
                      }))}
                      placeholder="-- Select Subscription Plan --"
                    />
                  )}
                  {errors.subscriptionPlanId && (
                    <p className="text-xs text-red-600 mt-1">{errors.subscriptionPlanId}</p>
                  )}
                  {!plansLoading && hospitalPlans.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No active hospital subscription plans configured. Add one under Subscriptions.
                    </p>
                  )}
                </div>
              )}
              <FormField label="Domain Name" name="domainName" value={form.domainName} onChange={handleChange} error={errors.domainName} required placeholder="e.g. cityhospital" />
              <FormField label="Official Email" name="officialEmail" value={form.officialEmail} onChange={handleChange} error={errors.officialEmail} required type="email" />
              <FormField label="Official Phone" name="officialPhone" value={form.officialPhone} onChange={handleChange} error={errors.officialPhone} required />
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Address */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Address</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Address Line 1" name="addressline1" value={form.addressline1 || ""} onChange={handleChange} className="sm:col-span-2" />
              <FormField label="Address Line 2" name="addressline2" value={form.addressline2 || ""} onChange={handleChange} className="sm:col-span-2" />
              <FormField label="City" name="city" value={form.city || ""} onChange={handleChange} />
              <FormField label="State" name="state" value={form.state || ""} onChange={handleChange} />
              <FormField label="Country" name="country" value={form.country || ""} onChange={handleChange} />
              <FormField label="Postal Code" name="postcode" value={form.postcode || ""} onChange={handleChange} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Zone</label>
                <CustomSelect
                  value={form.timezone || ""}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, timezone: val }));
                    if (errors.timezone) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.timezone;
                        return next;
                      });
                    }
                  }}
                  options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
                  placeholder="Select timezone"
                />
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Contact Person */}
          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">Contact Person</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Contact Person Name" name="contactPersonName" value={form.contactPersonName || ""} onChange={handleChange} />
              <FormField label="Contact Person Email" name="contactPersonEmail" value={form.contactPersonEmail || ""} onChange={handleChange} error={errors.contactPersonEmail} type="email" />
              <FormField label="Contact Person Phone" name="contactPersonPhone" value={form.contactPersonPhone || ""} onChange={handleChange} error={errors.contactPersonPhone} />
            </div>
          </div>

          {/* System Status (edit only) */}
          {isEdit && (
            <>
              <hr className="border-gray-100" />
              <div>
                <h2 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">System Status</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Access</label>
                    <CustomSelect
                      value={status}
                      onChange={(val) => setStatus(val as HospitalStatus)}
                      options={[
                        { value: "ACTIVE", label: "ACTIVE" },
                        { value: "INACTIVE", label: "INACTIVE" },
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Onboarding Status</label>
                    <CustomSelect
                      value={onboardingStatus}
                      onChange={(val) => setOnboardingStatus(val as OnboardingStatus)}
                      options={
                        hospitalOnboardingStatus === "COMPLETED"
                          ? [{ value: "COMPLETED", label: "COMPLETED" }]
                          : [
                              { value: "NOT_STARTED", label: "NOT STARTED" },
                              { value: "IN_PROGRESS", label: "IN PROGRESS" },
                              { value: "COMPLETED", label: "COMPLETED" },
                            ]
                      }
                      disabled={hospitalOnboardingStatus === "COMPLETED"}
                    />
                    {hospitalOnboardingStatus === "COMPLETED" && (
                      <p className="text-xs text-gray-400 mt-1">Onboarding status completed cannot be changed.</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <hr className="border-gray-100" />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={() => navigate("/hospitals")}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? "Update Hospital" : "Create Hospital"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// --- Helper Components ---

const FormField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  type = "text",
  placeholder,
  className,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  className?: string;
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
        error ? "border-red-300 bg-red-50" : "border-gray-200"
      }`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

export default HospitalForm;
